import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { createVehicle, updateVehicle, uploadFile, getFileUrl } from '../../lib/supabase'
import { decodeVIN, buildEngineString } from '../../lib/vinDecoder'
import { calculateVehicleScore, getScoreTier } from '../../lib/garageScore'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Car,
  Loader,
  Upload,
  Sparkles,
  X,
  Lock,
  Award,
  FileText
} from 'lucide-react'

// Common makes for quick selection
const POPULAR_MAKES = [
  'Porsche', 'Ferrari', 'Lamborghini', 'Mercedes-Benz', 'BMW',
  'Audi', 'Land Rover', 'Ford', 'Chevrolet', 'Toyota'
]

export default function QuickAddVehicle() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [step, setStep] = useState(1) // 1: Basic, 2: Photos, 3: Done
  const [loading, setLoading] = useState(false)
  const [vinLoading, setVinLoading] = useState(false)
  const [error, setError] = useState('')
  const [vehicleId, setVehicleId] = useState(null)

  const [formData, setFormData] = useState({
    year: '',
    make: '',
    model: '',
    vin: '',
    // Optional fields populated by VIN
    trim: '',
    color: '',
    fuel_type: '',
    engine_info: '',
    drive_type: ''
  })

  const [selectedFiles, setSelectedFiles] = useState([])
  const [imageUrls, setImageUrls] = useState([])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [garageScore, setGarageScore] = useState(null)
  const [scoreTier, setScoreTier] = useState(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  // Detect mobile/touch device
  const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    ('ontouchstart' in window && window.innerWidth < 1024)

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 80 }, (_, i) => currentYear - i)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleMakeSelect = (make) => {
    setFormData(prev => ({ ...prev, make }))
  }

  const handleVINLookup = async () => {
    const vin = formData.vin.trim().toUpperCase()
    if (!vin || vin.length !== 17) return

    setVinLoading(true)
    try {
      const decoded = await decodeVIN(vin)
      setFormData(prev => ({
        ...prev,
        vin,
        year: decoded.year || prev.year,
        make: decoded.make || prev.make,
        model: decoded.model || prev.model,
        trim: decoded.trim || prev.trim,
        fuel_type: decoded.fuelType || prev.fuel_type,
        engine_info: buildEngineString(decoded) || prev.engine_info,
        drive_type: decoded.driveType || prev.drive_type
      }))
    } catch (err) {
      console.error('VIN lookup failed:', err)
    } finally {
      setVinLoading(false)
    }
  }

  // Auto-lookup VIN when 17 characters entered
  useEffect(() => {
    if (formData.vin.length === 17) {
      handleVINLookup()
    }
  }, [formData.vin])

  // Compress image before upload
  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        try {
          let { width, height } = img
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob(
            (blob) => {
              URL.revokeObjectURL(objectUrl)
              if (blob) {
                resolve(blob)
              } else {
                // toBlob failed, return original file as fallback
                resolve(file)
              }
            },
            'image/jpeg',
            quality
          )
        } catch (err) {
          URL.revokeObjectURL(objectUrl)
          resolve(file) // fallback to original on any canvas error
        }
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(file) // fallback to original if image can't load
      }
      img.src = objectUrl
    })
  }

  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return
    const fileArray = Array.from(files).filter(file =>
      file.type.startsWith('image/') || file.name.match(/\.(jpg|jpeg|png|heic|heif|webp)$/i)
    )
    if (fileArray.length > 0) {
      setSelectedFiles(prev => [...prev, ...fileArray])
    }
  }

  const handleDragDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    handleFileSelect(files)
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const removeImage = (index) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!formData.year || !formData.make || !formData.model) {
      setError('Please fill in Year, Make, and Model')
      return
    }

    setLoading(true)
    setError('')

    try {
      const vehicleData = {
        owner_id: user.id,
        year: parseInt(formData.year),
        make: formData.make,
        model: formData.model,
        trim: formData.trim || null,
        vin: formData.vin || null,
        color: formData.color || null,
        status: 'ready',
        primary_image_url: null,
        fuel_type: formData.fuel_type || null,
        engine_info: formData.engine_info || null,
        drive_type: formData.drive_type || null
      }

      // Upload files if any selected
      let uploadedUrls = []
      if (selectedFiles.length > 0) {
        setUploadProgress(0)
      }

      // Create vehicle first so we have the ID for photo paths
      const { data, error: createError } = await createVehicle(vehicleData)
      if (createError) throw createError

      const newVehicleId = data?.id || data?.[0]?.id

      // Now upload photos with real vehicleId
      if (selectedFiles.length > 0 && newVehicleId) {
        const totalFiles = selectedFiles.length
        for (let i = 0; i < selectedFiles.length; i++) {
          try {
            const file = selectedFiles[i]

            // Compress image (falls back to original on failure)
            let fileToUpload = file
            try {
              const compressedBlob = await compressImage(file)
              if (compressedBlob && compressedBlob instanceof Blob) {
                fileToUpload = new File([compressedBlob], file.name || `photo-${i}.jpg`, { type: 'image/jpeg' })
              }
            } catch (compressErr) {
              console.warn(`Compression failed for file ${i}, using original:`, compressErr)
            }

            // Create unique filename within the bucket
            const timestamp = Date.now()
            const filePath = `${user.id}/${newVehicleId}/${timestamp}-${i}.jpg`

            // Upload to Supabase storage
            const { error: uploadError } = await uploadFile('vehicle-images', filePath, fileToUpload)
            if (uploadError) {
              console.error(`Upload error for file ${i}:`, uploadError)
              continue
            }

            // Get public URL
            const url = getFileUrl('vehicle-images', filePath)
            uploadedUrls.push(url)

            setUploadProgress(Math.floor(((i + 1) / totalFiles) * 100))
          } catch (uploadErr) {
            console.error(`Error uploading file ${i}:`, uploadErr)
          }
        }

        // Update vehicle with primary image URL
        if (uploadedUrls.length > 0) {
          await updateVehicle(newVehicleId, { primary_image_url: uploadedUrls[0] })
        }
      }
      setVehicleId(newVehicleId)

      // Calculate score
      const score = calculateVehicleScore(formData, 0, 0, 0, uploadedUrls.length)
      const tier = getScoreTier(score)
      setGarageScore(score)
      setScoreTier(tier)

      setStep(3)
    } catch (err) {
      console.error('Error saving vehicle:', err)
      setError(err.message || 'Failed to save vehicle')
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  const canProceed = formData.year && formData.make && formData.model

  // Step 1: Basic Info
  if (step === 1) {
    return (
      <div className="animate-fade-in max-w-2xl mx-auto">
        <Link to="/garage" className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary mb-6">
          <ArrowLeft size={20} />
          Back to Garage
        </Link>

        <div className="text-center mb-8">
          <h1 className="page-title">Add a Car</h1>
          <p className="text-rdc-taupe mt-2">Just the basics — you can add details later</p>
        </div>

        {/* VIN Quick Entry */}
        <div className="card p-6 mb-6 bg-gradient-to-r from-rdc-primary/5 to-transparent">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles size={20} className="text-rdc-primary" />
            <span className="font-medium text-black">Have a VIN? We'll fill it in for you</span>
          </div>
          <div className="flex gap-3">
            <input
              name="vin"
              value={formData.vin}
              onChange={handleChange}
              className="input-field flex-1 font-mono uppercase"
              placeholder="Enter 17-character VIN"
              maxLength={17}
            />
            {vinLoading && (
              <div className="flex items-center px-4">
                <Loader size={20} className="animate-spin text-rdc-primary" />
              </div>
            )}
          </div>
          {formData.vin.length > 0 && formData.vin.length < 17 && (
            <p className="text-xs text-rdc-taupe mt-2">{17 - formData.vin.length} more characters needed</p>
          )}
        </div>

        <div className="card p-6">
          <div className="space-y-6">
            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Year *</label>
              <select
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select year</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Make */}
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Make *</label>
              <input
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="input-field mb-3"
                placeholder="e.g., Porsche"
              />
              <div className="flex flex-wrap gap-2">
                {POPULAR_MAKES.map(make => (
                  <button
                    key={make}
                    type="button"
                    onClick={() => handleMakeSelect(make)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                      formData.make === make
                        ? 'bg-rdc-primary text-white'
                        : 'bg-rdc-cream text-rdc-dark-gray hover:bg-rdc-warm-gray/50'
                    }`}
                  >
                    {make}
                  </button>
                ))}
              </div>
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Model *</label>
              <input
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g., 911 GT3"
              />
            </div>

            {/* Optional: Trim & Color in same row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Trim <span className="text-rdc-taupe font-normal">(optional)</span>
                </label>
                <input
                  name="trim"
                  value={formData.trim}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Touring"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Color <span className="text-rdc-taupe font-normal">(optional)</span>
                </label>
                <input
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Guards Red"
                />
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          )}

          <div className="mt-8 space-y-4">
            <div className="flex justify-end gap-4">
              <Link to="/garage" className="btn-secondary">Cancel</Link>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceed}
                className="btn-primary flex items-center gap-2"
              >
                Next: Add Photos
                <ArrowRight size={18} />
              </button>
            </div>
            <Link
              to="/garage/add/full"
              className="block text-center text-sm text-rdc-taupe hover:text-rdc-primary transition-colors"
            >
              Need more options? Use the detailed form
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Step 2: Photos
  if (step === 2) {
    return (
      <div className="animate-fade-in max-w-2xl mx-auto">
        <button
          onClick={() => setStep(1)}
          className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary mb-6"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div className="text-center mb-8">
          <h1 className="page-title">Add Photos</h1>
          <p className="text-rdc-taupe mt-2">
            {formData.year} {formData.make} {formData.model}
          </p>
        </div>

        <div className="card p-6 mb-6">
          {/* File Upload with Drag & Drop */}
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-rdc-primary') }}
            onDragLeave={(e) => { e.currentTarget.classList.remove('border-rdc-primary') }}
            onDrop={handleDragDrop}
            className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-rdc-warm-gray rounded-xl cursor-pointer hover:border-rdc-primary transition-colors"
          >
            <Upload size={32} className="text-rdc-taupe mb-3" />
            <span className="text-sm font-medium text-black">
              {isMobile ? 'Add photos from your library or camera' : 'Upload vehicle photos'}
            </span>
            <span className="text-xs text-rdc-taupe mt-1">
              {isMobile ? 'Tap below to choose' : 'Drag & drop photos or click to browse'}
            </span>
            <span className="text-xs text-rdc-taupe mt-2">PNG, JPG up to 10MB each</span>
            {/* Standard file picker (library/files) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Camera capture input (mobile only) */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 text-sm font-medium text-rdc-primary hover:bg-rdc-cream rounded-lg transition-colors"
              >
                {isMobile ? 'Choose from library' : 'Browse files'}
              </button>
              {isMobile && (
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="px-4 py-2 text-sm font-medium text-white bg-rdc-primary hover:bg-rdc-primary/90 rounded-lg transition-colors"
                >
                  Take a photo
                </button>
              )}
            </div>
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium text-rdc-dark-gray mb-3">Selected Photos ({selectedFiles.length})</div>
              <div className="grid grid-cols-3 gap-3">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="relative aspect-video bg-rdc-cream rounded-lg overflow-hidden group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name || `Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center"
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="mt-6">
              <div className="text-sm font-medium text-rdc-dark-gray mb-2">Uploading photos...</div>
              <div className="w-full bg-rdc-cream rounded-full h-2">
                <div
                  className="bg-rdc-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-xs text-rdc-taupe mt-2">{uploadProgress}% complete</p>
            </div>
          )}
        </div>

        {error && (
          <p className="mb-4 text-sm text-red-600">{error}</p>
        )}

        <div className="flex justify-between">
          <button
            onClick={handleSubmit}
            className="text-rdc-taupe hover:text-rdc-dark-gray text-sm"
          >
            Skip photos for now
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Save to Garage
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  // Step 3: Success
  return (
    <div className="animate-fade-in max-w-2xl mx-auto py-12">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-green-500" />
        </div>
        <h1 className="font-display text-2xl font-semibold text-black mb-3">
          {formData.year} {formData.make} {formData.model}
        </h1>
        <p className="text-rdc-taupe mb-8">Added to your garage!</p>
      </div>

      {/* Garage Score Card */}
      {garageScore !== null && scoreTier && (
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-black mb-1">Your {formData.year} {formData.make}</h2>
              <p className="text-sm text-rdc-taupe">is a {scoreTier.label}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-rdc-primary">{garageScore}</div>
              <p className="text-xs text-rdc-taupe">Provenance Score</p>
            </div>
          </div>

          {/* Score Tier Badge */}
          <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-gradient-to-r from-rdc-primary/5 to-transparent rounded-lg">
            <Award size={18} className="text-rdc-primary" />
            <span className="text-sm font-medium text-black">{scoreTier.label}</span>
            <span className="text-xs text-rdc-taupe">— add photos and service records to level up!</span>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-rdc-dark-gray">Score Progress</span>
              <span className="text-xs text-rdc-taupe">{garageScore} / 100</span>
            </div>
            <div className="w-full bg-rdc-cream rounded-full h-2">
              <div
                className="bg-rdc-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(garageScore, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Action Items */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-rdc-dark-gray mb-3">Quick wins to improve your score:</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-rdc-cream rounded-lg">
                <Lock size={16} className="text-rdc-taupe" />
                <span className="text-sm text-rdc-dark-gray">Add photos <span className="text-rdc-primary font-medium">+20 pts</span></span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-rdc-cream rounded-lg">
                <FileText size={16} className="text-rdc-taupe" />
                <span className="text-sm text-rdc-dark-gray">Add a service record <span className="text-rdc-primary font-medium">+20 pts</span></span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-rdc-cream rounded-lg">
                <Upload size={16} className="text-rdc-taupe" />
                <span className="text-sm text-rdc-dark-gray">Upload documents <span className="text-rdc-primary font-medium">+15 pts</span></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Link
          to={vehicleId ? `/garage/${vehicleId}` : '/garage'}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          <Car size={18} />
          View in Garage
        </Link>
        <button
          onClick={() => {
            setStep(1)
            setFormData({ year: '', make: '', model: '', vin: '', trim: '', color: '' })
            setSelectedFiles([])
            setImageUrls([])
            setVehicleId(null)
            setGarageScore(null)
            setScoreTier(null)
          }}
          className="btn-secondary w-full"
        >
          Add Another Car
        </button>
      </div>
    </div>
  )
}
